import { Html } from '@react-three/drei';
import { useMemo } from 'react';
import { Quaternion, Vector3 } from 'three';
import type { Graph3DPositionedNode } from '../../core/graph.types';
import type { Graph3DEdgeRendererProps } from '../../core/renderer.types';
import { HTML_TRANSFORM_BASE_DISTANCE_FACTOR } from '../../utils/coordTransforms';

const up = new Vector3(0, 1, 0);

export function EdgeTube3D({ edge, runtime, theme }: Graph3DEdgeRendererProps) {
  const edgeKind = getEdgeDataString(edge.data, 'edgeKind');
  const isOrgEdge = edgeKind === 'org';
  const routeStyle = getEdgeDataString(edge.data, 'routeStyle');
  const showArrow = getEdgeDataBoolean(edge.data, 'showArrow', !isOrgEdge);
  const { segments, targetPoint, targetQuaternion, labelPoint } =
    useMemo(() => {
      const points = isOrgEdge
        ? getOrgEdgePoints(edge.sourceNode, edge.targetNode)
        : routeStyle === 'orthogonal'
          ? getProcessSideEdgePoints(edge.sourceNode, edge.targetNode)
          : getStraightEdgePoints(edge.sourceNode, edge.targetNode);
      const builtSegments = points.slice(1).flatMap((point, index) => {
        const segment = createSegment(points[index], point);
        return segment ? [segment] : [];
      });
      const lastPoint = points[points.length - 1];
      const previousPoint = points[points.length - 2] ?? lastPoint;
      const direction = lastPoint.clone().sub(previousPoint);
      const normalized =
        direction.lengthSq() === 0 ? up : direction.normalize();
      return {
        segments: builtSegments,
        targetPoint: lastPoint,
        targetQuaternion: new Quaternion().setFromUnitVectors(up, normalized),
        midpoint: getPolylineMidpoint(points),
        // 角色/结果折线的文案贴在更靠近分叉点的位置，再通过上下偏移形成 2D 同款效果。
        labelPoint:
          routeStyle === 'orthogonal' && points.length >= 2
            ? points[0].clone().lerp(points[1], 0.84)
            : getPolylineMidpoint(points),
      };
    }, [edge.sourceNode, edge.targetNode, isOrgEdge, routeStyle]);

  const edgeThemeColor = getEdgeDataString(edge.data, 'themeColor');
  const color =
    runtime?.color ??
    (runtime?.active || runtime?.highlighted
      ? edgeThemeColor || theme.edge.activeColor
      : theme.edge.color);
  const labelOffsetY = getEdgeDataNumber(
    edge.data,
    'labelOffsetY',
    edgeKind === 'role' ? 18 : edgeKind === 'result' ? 10 : 10
  );
  const labelOffsetX = getEdgeDataNumber(edge.data, 'labelOffsetX', 0);
  const radius = isOrgEdge
    ? runtime?.active || runtime?.highlighted
      ? 1.2
      : 0.82
    : runtime?.active || runtime?.highlighted
      ? 1.8
      : 1.15;

  return (
    <group>
      {segments.map((segment, index) => (
        <mesh
          key={index}
          position={segment.midpoint}
          quaternion={segment.quaternion}
        >
          <cylinderGeometry args={[radius, radius, segment.length, 10]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={runtime?.flow ? 0.45 : 0.12}
            transparent
            opacity={runtime?.hidden ? 0.12 : 0.86}
          />
        </mesh>
      ))}
      {showArrow && (
        <mesh position={targetPoint} quaternion={targetQuaternion}>
          <coneGeometry args={[7, 18, 18]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={0.25}
            transparent
            opacity={runtime?.hidden ? 0.12 : 0.92}
          />
        </mesh>
      )}
      {edge.label && (
        <Html
          center
          transform
          distanceFactor={HTML_TRANSFORM_BASE_DISTANCE_FACTOR}
          position={[
            labelPoint.x + labelOffsetX,
            labelPoint.y + labelOffsetY,
            labelPoint.z,
          ]}
        >
          <div
            style={{
              color,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              textShadow: '0 1px 2px rgba(255,255,255,.9)',
            }}
          >
            {edge.label}
          </div>
        </Html>
      )}
    </group>
  );
}

function getPolylineMidpoint(points: Vector3[]): Vector3 {
  if (points.length === 0) return new Vector3();
  if (points.length === 1) return points[0].clone();
  const segmentLengths = points
    .slice(1)
    .map((point, index) => point.distanceTo(points[index]));
  const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
  if (totalLength === 0) return points[0].clone();
  let remaining = totalLength / 2;
  for (let index = 0; index < segmentLengths.length; index += 1) {
    const length = segmentLengths[index];
    if (remaining <= length) {
      const ratio = length === 0 ? 0 : remaining / length;
      return points[index].clone().lerp(points[index + 1], ratio);
    }
    remaining -= length;
  }
  return points[points.length - 1].clone();
}

function getStraightEdgePoints(
  sourceNode: Graph3DPositionedNode,
  targetNode: Graph3DPositionedNode
): Vector3[] {
  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);
  return [
    getNodeBoundaryPoint(sourceNode, targetCenter),
    getNodeBoundaryPoint(targetNode, sourceCenter),
  ];
}

function getOrgEdgePoints(
  sourceNode: Graph3DPositionedNode,
  targetNode: Graph3DPositionedNode
): Vector3[] {
  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);
  const sourceBottom = new Vector3(
    sourceCenter.x,
    sourceCenter.y - sourceNode.resolvedSize.height / 2,
    sourceCenter.z
  );
  if (sourceNode.type === 'company') {
    const targetTop = new Vector3(
      targetCenter.x,
      targetCenter.y + targetNode.resolvedSize.height / 2,
      targetCenter.z
    );
    const verticalGap = sourceBottom.y - targetTop.y;
    const turn = Math.max(42, Math.min(88, verticalGap * 0.62));
    const turnY = sourceBottom.y - turn;
    return [
      sourceBottom,
      new Vector3(sourceBottom.x, turnY, sourceBottom.z),
      new Vector3(targetTop.x, turnY, targetTop.z),
      targetTop,
    ];
  }
  const targetLeft = new Vector3(
    targetCenter.x - targetNode.resolvedSize.width / 2,
    targetCenter.y,
    targetCenter.z
  );
  const verticalGap = sourceBottom.y - targetLeft.y;
  const turnY = verticalGap < 105 ? sourceBottom.y - 84 : targetLeft.y;
  return [
    sourceBottom,
    new Vector3(sourceBottom.x, turnY, sourceBottom.z),
    new Vector3(targetLeft.x, turnY, targetLeft.z),
    targetLeft,
  ];
}

function getProcessSideEdgePoints(
  sourceNode: Graph3DPositionedNode,
  targetNode: Graph3DPositionedNode
): Vector3[] {
  const sourceCenter = getNodeCenter(sourceNode);
  const sourceRight = new Vector3(
    sourceCenter.x + sourceNode.resolvedSize.width / 2,
    sourceCenter.y,
    sourceCenter.z
  );
  const targetLeft = getNodeBoundaryPoint(targetNode, sourceRight);
  const horizontalGap = targetLeft.x - sourceRight.x;
  const turnX = sourceRight.x + Math.max(72, horizontalGap * 0.56);
  return [
    sourceRight,
    new Vector3(turnX, sourceRight.y, sourceRight.z),
    new Vector3(turnX, targetLeft.y, sourceRight.z),
    new Vector3(targetLeft.x, targetLeft.y, sourceRight.z),
  ];
}

function createSegment(source: Vector3, target: Vector3) {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < 1) return null;
  return {
    midpoint: source.clone().add(target).multiplyScalar(0.5),
    length,
    quaternion: new Quaternion().setFromUnitVectors(up, direction.normalize()),
  };
}

function getNodeCenter(node: Graph3DPositionedNode): Vector3 {
  return new Vector3(
    node.resolvedPosition.x,
    node.resolvedPosition.y,
    node.resolvedPosition.z
  );
}

function getNodeBoundaryPoint(
  node: Graph3DPositionedNode,
  toward: Vector3
): Vector3 {
  const center = getNodeCenter(node);
  const direction = toward.clone().sub(center);
  if (direction.lengthSq() === 0) return center;
  const halfWidth = Math.max(node.resolvedSize.width / 2, 1);
  const halfHeight = Math.max(node.resolvedSize.height / 2, 1);
  const ratio = Math.max(
    Math.abs(direction.x) / halfWidth,
    Math.abs(direction.y) / halfHeight,
    0.001
  );
  return center.add(direction.multiplyScalar(1 / ratio));
}

function getEdgeDataString(data: unknown, key: string): string {
  if (!data || typeof data !== 'object') return '';
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
}

function getEdgeDataNumber(
  data: unknown,
  key: string,
  fallback: number
): number {
  if (!data || typeof data !== 'object') return fallback;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : fallback;
}

function getEdgeDataBoolean(
  data: unknown,
  key: string,
  fallback: boolean
): boolean {
  if (!data || typeof data !== 'object') return fallback;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'boolean' ? value : fallback;
}

